/*
 * <card-pack-selector>: pack dropdown + "Add to cart" on product cards (snippets/card-pack-selector.liquid).
 *
 * - Packs are the Kaching Bundles quantity tiers, loaded once per page through window.cdKaching
 *   (assets/cart-drawer-volume.js). Prices are display only: Kaching's discount function prices
 *   the line in the cart, so a pack is simply "add N cans".
 * - The list opens as a popover above the field (native details). With data-mobile-style="sheet",
 *   small screens move the same list into one shared bottom sheet (dialog) and back on close.
 * - The chosen pack sets the quantity of the theme's product-form, which adds and opens the drawer.
 * - All wording comes from theme settings via data attributes.
 */
(() => {
  if (window.__cpsLoaded) return;
  window.__cpsLoaded = true;

  const mobile = window.matchMedia('(max-width: 699px)');
  let sheet = null;

  const getSheet = () => {
    if (sheet) return sheet;
    sheet = document.createElement('dialog');
    sheet.className = 'cps-sheet';
    // Tap on the backdrop closes it
    sheet.addEventListener('click', (event) => event.target === sheet && sheet.close());
    document.body.append(sheet);
    return sheet;
  };

  const chevron = '<svg class="cps__chevron" aria-hidden="true" focusable="false" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const check = '<svg aria-hidden="true" focusable="false" width="12" height="10" viewBox="0 0 12 10"><path d="M1 5l3.5 3.5L11 1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template);

  const whenKaching = () => (window.cdKaching
    ? Promise.resolve(window.cdKaching)
    : new Promise((resolve) => document.addEventListener('cd-kaching:ready', () => resolve(window.cdKaching), { once: true })));

  class CardPackSelector extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-cps-details]');
      if (!this.details) return;
      this.panel = this.querySelector('[data-cps-panel]');
      this.quantityInput = this.querySelector('[data-cps-quantity]');

      this.details.addEventListener('toggle', () => this.onToggle());
      this.panel.addEventListener('change', (event) => this.onSelect(event));
      this.panel.addEventListener('click', (event) => event.target.closest('[data-cps-close]') && this.close());
      this.querySelector('form')?.addEventListener('cart:error', () => this.showError());
      this.querySelector('form')?.addEventListener('submit', () => this.showError(false));

      this._outside = (event) => this.details.open && !this.contains(event.target) && this.close();
      document.addEventListener('click', this._outside);

      whenKaching().then((kaching) => {
        this.kaching = kaching;
        return kaching.loadCardDeals(this.dataset.handle);
      }).then((deals) => this.isConnected && this.render(deals));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._outside);
    }

    render(deals) {
      const { pickDeal, unitPrice, formatMoney, escapeHtml } = this.kaching;
      const deal = pickDeal(deals, this);
      const price = parseInt(this.dataset.price, 10);
      if (!deal || !price) return; // No Kaching deal: the form still adds 1 can

      const stock = this.dataset.stock === '' ? Infinity : parseInt(this.dataset.stock, 10);
      const text = (key) => escapeHtml(this.dataset[key] || '');

      this.packs = deal.bars.map((bar) => {
        const unit = unitPrice(price, bar);
        const percent = bar.type === 'percentage' && bar.value > 0 ? bar.value : 0;
        return {
          quantity: bar.quantity,
          percent,
          disabled: bar.quantity > stock,
          name: fill(text('packLabel') || '[qty]', { qty: bar.quantity }),
          total: formatMoney(unit * bar.quantity),
          perCan: fill(text('perCanLabel') || '[price]', { price: formatMoney(unit) }),
          badge: percent ? fill(text('discountLabel'), { percent }) : ''
        };
      });

      const available = this.packs.filter((pack) => !pack.disabled);
      if (!available.length) return;
      const wanted = parseInt(this.dataset.defaultQty, 10) || 1;
      const initial = [...available].reverse().find((pack) => pack.quantity <= wanted) || available[0];

      const name = `cps-${this.dataset.productId}-${Math.random().toString(36).slice(2, 7)}`;
      this.querySelector('[data-cps-options]').insertAdjacentHTML('beforeend', this.packs.map((pack) => `
        <label class="cps__option">
          <input type="radio" name="${name}" value="${pack.quantity}" ${pack === initial ? 'checked' : ''} ${pack.disabled ? 'disabled' : ''}>
          <span class="cps__option-name">${pack.name}</span>
          <span class="cps__option-price">
            ${pack.badge ? `<span class="cps__badge">${pack.badge}</span>` : ''}
            <strong>${pack.total}</strong>
          </span>
          <span class="cps__radio" aria-hidden="true">${check}</span>
        </label>`).join(''));

      this.select(initial);
      this.renderCorner(deal);
      this.details.hidden = false;
      this.classList.add('is-ready');
    }

    renderCorner(deal) {
      const badge = this.closest('product-card')?.querySelector('[data-cps-corner]');
      const template = this.dataset.cornerText;
      if (!badge || !template) return;

      const target = parseInt(this.dataset.cornerQty, 10) || 0;
      const discounted = deal.bars.filter((bar) => bar.type === 'percentage' && bar.value > 0);
      const bar = target ? discounted.find((b) => b.quantity === target) : discounted[discounted.length - 1];
      if (!bar) return;

      badge.textContent = fill(template, { percent: bar.value, qty: bar.quantity });
      badge.hidden = false;
    }

    select(pack) {
      this.quantityInput.value = pack.quantity;
      this.querySelector('[data-cps-summary]').innerHTML = `
        <span class="cps__summary-name">${pack.name}${chevron}</span>
        <span class="cps__summary-price">
          <strong>${pack.total}</strong>
          <small>${pack.badge ? `<span class="cps__badge">${pack.badge}</span>` : ''}<span>${pack.perCan}</span></small>
        </span>`;
    }

    onSelect(event) {
      const pack = this.packs?.find((p) => String(p.quantity) === event.target.value);
      if (!pack) return;
      this.select(pack);
      this.showError(false);
      this.close();
    }

    onToggle() {
      if (!this.details.open || !mobile.matches || this.dataset.mobileStyle !== 'sheet') return;
      // Small screens: show the list as a bottom sheet instead of over the narrow card
      this.details.open = false;
      const dialog = getSheet();
      ['--cps-accent', '--cps-badge'].forEach((name) => dialog.style.setProperty(name, getComputedStyle(this).getPropertyValue(name)));
      dialog.replaceChildren(this.panel);
      dialog.addEventListener('close', () => this.details.append(this.panel), { once: true });
      dialog.showModal();
    }

    close() {
      if (sheet?.open && sheet.contains(this.panel)) sheet.close();
      this.details.open = false;
    }

    showError(show = true) {
      const error = this.querySelector('[data-cps-error]');
      if (!error) return;
      error.textContent = show ? this.dataset.errorText : '';
      error.hidden = !show;
    }
  }

  if (!customElements.get('card-pack-selector')) customElements.define('card-pack-selector', CardPackSelector);
})();
